// Error report formatter for non-technical users

export interface FormattedErrorReport {
  summary: {
    errorType: string;
    errorMessage: string;
    timestamp: string;
    userAction: string;
    severity: 'Low' | 'Medium' | 'High' | 'Critical';
  };
  userEnvironment: {
    browser: string;
    operatingSystem: string;
    device: string;
    screenResolution: string;
    internetConnection: string;
  };
  applicationContext: {
    pageUrl: string;
    userRole: string;
    sessionId: string;
    timeOnPage: string;
  };
  technicalDetails: {
    errorStack: string;
    componentStack?: string;
    performanceMetrics: string;
    memoryUsage: string;
  };
}

// Determine error severity based on error type and context
const getErrorSeverity = (error: Error, context?: string): 'Low' | 'Medium' | 'High' | 'Critical' => {
  const errorMessage = error.message.toLowerCase();
  const errorName = error.name.toLowerCase();
  
  // Critical errors
  if (errorMessage.includes('memory') || errorMessage.includes('out of memory')) {
    return 'Critical';
  }
  
  // High severity errors
  if (errorName.includes('typeerror') || errorName.includes('referenceerror')) {
    return 'High';
  }
  
  // Medium severity errors
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('api')) {
    return 'Medium';
  }
  
  // Low severity errors
  return 'Low';
};

// Get user-friendly error type
const getErrorType = (error: Error): string => {
  const errorName = error.name;
  const errorMessage = error.message.toLowerCase();
  
  if (errorName === 'TypeError') return 'Data Processing Error';
  if (errorName === 'ReferenceError') return 'Code Reference Error';
  if (errorName === 'SyntaxError') return 'Code Syntax Error';
  if (errorMessage.includes('network')) return 'Network Connection Error';
  if (errorMessage.includes('fetch')) return 'Data Loading Error';
  if (errorMessage.includes('memory')) return 'Memory Usage Error';
  if (errorMessage.includes('timeout')) return 'Operation Timeout Error';
  if (errorMessage.includes('permission')) return 'Permission Access Error';
  
  return 'Application Error';
};

// Get user action description
const getUserAction = (context?: string): string => {
  if (!context) return 'Unknown action';
  
  const contextLower = context.toLowerCase();
  
  if (contextLower.includes('login')) return 'User was logging in';
  if (contextLower.includes('signup')) return 'User was creating account';
  if (contextLower.includes('upload')) return 'User was uploading file';
  if (contextLower.includes('download')) return 'User was downloading file';
  if (contextLower.includes('submit')) return 'User was submitting form';
  if (contextLower.includes('search')) return 'User was searching';
  if (contextLower.includes('payment')) return 'User was making payment';
  if (contextLower.includes('interview')) return 'User was in interview';
  if (contextLower.includes('resume')) return 'User was working with resume';
  if (contextLower.includes('api')) return 'User was loading data';
  if (contextLower.includes('async')) return 'User was waiting for operation';
  
  return 'User was performing an action';
};

// Get user-friendly role name
const getUserRoleName = (userRole?: string): string => {
  if (!userRole) return 'Not logged in';
  
  const roleNumber = parseInt(userRole);
  switch (roleNumber) {
    case 1:
      return 'Recruiter';
    case 2:
      return 'Job Seeker';
    case 3:
      return 'New User';
    default:
      return `Role ${userRole}`;
  }
};

// Get browser information in user-friendly format
const getBrowserInfo = (browser: any): string => {
  return `${browser.name} ${browser.version} (${browser.engine})`;
};

// Get OS information in user-friendly format
const getOSInfo = (os: any): string => {
  return `${os.name} ${os.version} (${os.platform})`;
};

// Get device information in user-friendly format
const getDeviceInfo = (device: any): string => {
  if (device.type === 'mobile') {
    return `Mobile device (${device.vendor} ${device.model})`;
  } else if (device.type === 'tablet') {
    return `Tablet device (${device.vendor} ${device.model})`;
  }
  return `Desktop computer (${device.vendor} ${device.model})`;
};

// Get screen resolution in user-friendly format
const getScreenResolution = (screen: any): string => {
  return `${screen.width} × ${screen.height} pixels`;
};

// Get internet connection info in user-friendly format
const getConnectionInfo = (connection: any): string => {
  if (connection.effectiveType === '4g') return 'Fast (4G)';
  if (connection.effectiveType === '3g') return 'Medium (3G)';
  if (connection.effectiveType === '2g') return 'Slow (2G)';
  if (connection.effectiveType === 'slow-2g') return 'Very Slow (2G)';
  return 'Unknown connection speed';
};

// Get performance metrics in user-friendly format
const getPerformanceMetrics = (performance: any): string => {
  const memory = performance.memory;
  if (memory) {
    const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
    const totalMB = Math.round(memory.totalJSHeapSize / 1024 / 1024);
    return `Memory: ${usedMB}MB used of ${totalMB}MB total`;
  }
  return 'Performance metrics not available';
};

// Get memory usage in user-friendly format
const getMemoryUsage = (performance: any): string => {
  const memory = performance.memory;
  if (memory) {
    const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
    const totalMB = Math.round(memory.totalJSHeapSize / 1024 / 1024);
    const percentage = Math.round((usedMB / totalMB) * 100);
    
    if (percentage > 80) return `High memory usage (${percentage}%)`;
    if (percentage > 60) return `Moderate memory usage (${percentage}%)`;
    return `Normal memory usage (${percentage}%)`;
  }
  return 'Memory usage not available';
};

// Format error report for non-technical users
export const formatErrorReport = (
  error: Error,
  systemInfo: any,
  context?: string,
  userRole?: string
): FormattedErrorReport => {
  const severity = getErrorSeverity(error, context);
  const errorType = getErrorType(error);
  const userAction = getUserAction(context);
  
  return {
    summary: {
      errorType,
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
      userAction,
      severity
    },
    userEnvironment: {
      browser: getBrowserInfo(systemInfo.browser),
      operatingSystem: getOSInfo(systemInfo.os),
      device: getDeviceInfo(systemInfo.device),
      screenResolution: getScreenResolution(systemInfo.device.screen),
      internetConnection: getConnectionInfo(systemInfo.network.connection)
    },
    applicationContext: {
      pageUrl: systemInfo.app.url,
      userRole: getUserRoleName(userRole),
      sessionId: systemInfo.app.sessionId,
      timeOnPage: 'Session duration not tracked'
    },
    technicalDetails: {
      errorStack: error.stack || 'No stack trace available',
      componentStack: (error as any).componentStack || 'No component stack available',
      performanceMetrics: getPerformanceMetrics(systemInfo.performance),
      memoryUsage: getMemoryUsage(systemInfo.performance)
    }
  };
};

// Generate HTML table for email
export const generateErrorReportHTML = (report: FormattedErrorReport): string => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { background-color: #f8f9fa; padding: 20px; border-radius: 5px; margin-bottom: 20px; }
        .severity-critical { color: #dc3545; font-weight: bold; }
        .severity-high { color: #fd7e14; font-weight: bold; }
        .severity-medium { color: #ffc107; font-weight: bold; }
        .severity-low { color: #28a745; font-weight: bold; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
        th { background-color: #f2f2f2; font-weight: bold; }
        .section { margin-bottom: 30px; }
        .section-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; color: #333; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>🚨 Error Report</h1>
        <p><strong>Error Type:</strong> ${report.summary.errorType}</p>
        <p><strong>Severity:</strong> <span class="severity-${report.summary.severity.toLowerCase()}">${report.summary.severity}</span></p>
        <p><strong>Time:</strong> ${new Date(report.summary.timestamp).toLocaleString()}</p>
      </div>

      <div class="section">
        <div class="section-title">📋 Error Summary</div>
        <table>
          <tr><th>Error Message</th><td>${report.summary.errorMessage}</td></tr>
          <tr><th>User Action</th><td>${report.summary.userAction}</td></tr>
          <tr><th>Severity Level</th><td class="severity-${report.summary.severity.toLowerCase()}">${report.summary.severity}</td></tr>
        </table>
      </div>

      <div class="section">
        <div class="section-title">💻 User Environment</div>
        <table>
          <tr><th>Browser</th><td>${report.userEnvironment.browser}</td></tr>
          <tr><th>Operating System</th><td>${report.userEnvironment.operatingSystem}</td></tr>
          <tr><th>Device</th><td>${report.userEnvironment.device}</td></tr>
          <tr><th>Screen Resolution</th><td>${report.userEnvironment.screenResolution}</td></tr>
          <tr><th>Internet Connection</th><td>${report.userEnvironment.internetConnection}</td></tr>
        </table>
      </div>

      <div class="section">
        <div class="section-title">🌐 Application Context</div>
        <table>
          <tr><th>Page URL</th><td>${report.applicationContext.pageUrl}</td></tr>
          <tr><th>User Role</th><td>${report.applicationContext.userRole}</td></tr>
          <tr><th>Session ID</th><td>${report.applicationContext.sessionId}</td></tr>
        </table>
      </div>

      <div class="section">
        <div class="section-title">🔧 Technical Details</div>
        <table>
          <tr><th>Performance Metrics</th><td>${report.technicalDetails.performanceMetrics}</td></tr>
          <tr><th>Memory Usage</th><td>${report.technicalDetails.memoryUsage}</td></tr>
        </table>
      </div>

      <div class="section">
        <div class="section-title">📊 Technical Stack Trace</div>
        <pre style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; overflow-x: auto; font-size: 12px;">${report.technicalDetails.errorStack}</pre>
      </div>
    </body>
    </html>
  `;
};

// Generate plain text version for email
export const generateErrorReportText = (report: FormattedErrorReport): string => {
  return `
🚨 ERROR REPORT
===============

📋 ERROR SUMMARY
----------------
Error Type: ${report.summary.errorType}
Error Message: ${report.summary.errorMessage}
User Action: ${report.summary.userAction}
Severity: ${report.summary.severity}
Time: ${new Date(report.summary.timestamp).toLocaleString()}

💻 USER ENVIRONMENT
-------------------
Browser: ${report.userEnvironment.browser}
Operating System: ${report.userEnvironment.operatingSystem}
Device: ${report.userEnvironment.device}
Screen Resolution: ${report.userEnvironment.screenResolution}
Internet Connection: ${report.userEnvironment.internetConnection}

🌐 APPLICATION CONTEXT
---------------------
Page URL: ${report.applicationContext.pageUrl}
User Role: ${report.applicationContext.userRole}
Session ID: ${report.applicationContext.sessionId}

🔧 TECHNICAL DETAILS
--------------------
Performance Metrics: ${report.technicalDetails.performanceMetrics}
Memory Usage: ${report.technicalDetails.memoryUsage}

📊 TECHNICAL STACK TRACE
------------------------
${report.technicalDetails.errorStack}
  `;
};

export default {
  formatErrorReport,
  generateErrorReportHTML,
  generateErrorReportText
}; 